<script setup lang="ts">
/**
 * WHAT IS LEFT BEFORE THIS VENUE CAN SELL, on Dnes.
 *
 * Specification: ../../test/setup-checklist.spec.ts. Gone once everything is
 * done or somebody hides it; hidden, too, if a question could not be answered,
 * rather than nagging about a step that may well be done.
 */
import { computed, onMounted, ref } from "vue";
import type { ConsoleApi } from "../api";
import type { ScreenId } from "../nav";
import { setupSteps, type SetupStep } from "../setup";

const props = defineProps<{
  api: Pick<ConsoleApi, "menu" | "venueSettings" | "staff" | "devices">;
  venueId: string;
  t: (key: string) => string;
}>();

const emit = defineEmits<{ navigate: [screen: ScreenId] }>();

const HIDDEN_KEY = `gf.office.setupHidden.${props.venueId}`;
const hidden = ref(
  (() => {
    try {
      return localStorage.getItem(HIDDEN_KEY) === "1";
    } catch {
      return false;
    }
  })(),
);
const steps = ref<readonly SetupStep[] | null>(null);

onMounted(async () => {
  if (hidden.value) return;
  try {
    const [menu, settings, staff, devices] = await Promise.all([
      props.api.menu(),
      props.api.venueSettings(),
      props.api.staff(),
      props.api.devices(),
    ]);
    steps.value = setupSteps({
      company: Boolean(settings.identity.legalName && settings.identity.registrationId),
      menuItems: menu.items.length,
      tables: settings.floor.length,
      people: staff.staff.filter((p) => p.active !== false).length,
      published: settings.published !== null && settings.published.current !== false,
      devices: devices.devices.length,
      pohoda: Boolean(settings.bookkeeping?.pohoda),
    });
  } catch {
    // Left unknown, and so not shown.
  }
});

const done = computed(() => steps.value?.filter((s) => s.done).length ?? 0);
const shown = computed(() => !hidden.value && steps.value !== null && done.value < steps.value.length);

const hide = (): void => {
  hidden.value = true;
  try {
    localStorage.setItem(HIDDEN_KEY, "1");
  } catch {
    // Hidden for this visit only.
  }
};

const say = (key: string, params: Record<string, string | number>): string =>
  Object.entries(params).reduce((text, [k, v]) => text.replace(`{${k}}`, String(v)), props.t(key));
</script>

<template>
  <section v-if="shown && steps" class="gf-panel setup" :aria-label="t('setup.heading')">
    <header class="setup__head">
      <div>
        <h2 class="gf-panel__title">{{ t("setup.heading") }}</h2>
        <p class="gf-muted setup__progress">{{ say("setup.progress", { done, total: steps.length }) }}</p>
      </div>
      <button type="button" class="gf-link gf-link--quiet" data-testid="setup-hide" @click="hide">
        {{ t("setup.hide") }}
      </button>
    </header>
    <ol class="setup__steps">
      <li v-for="step in steps" :key="step.id" class="setup__step" :class="{ 'setup__step--done': step.done }" :data-step="step.id">
        <span class="setup__mark" aria-hidden="true">{{ step.done ? "✓" : "" }}</span>
        <span class="setup__label">
          {{ t(`setup.step.${step.id}`) }}
          <span v-if="step.count !== undefined && step.count > 0" class="gf-muted">· {{ step.count }}</span>
        </span>
        <button v-if="!step.done" type="button" class="gf-button setup__open" @click="emit('navigate', step.screen)">
          {{ t("setup.open") }}
        </button>
      </li>
    </ol>
  </section>
</template>

<style scoped>
.setup {
  margin-bottom: var(--gf-space-4);
}

.setup__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--gf-space-3);
}

.setup__progress {
  margin: var(--gf-space-1) 0 0;
  font-size: var(--gf-text-sm);
}

.setup__steps {
  margin: var(--gf-space-3) 0 0;
  padding: 0;
  list-style: none;
}

.setup__step {
  display: flex;
  align-items: center;
  gap: var(--gf-space-3);
  min-height: 40px;
  border-top: var(--gf-border-thin) solid var(--gf-color-border-subtle);
}

.setup__mark {
  width: 1.25rem;
  text-align: center;
  color: var(--gf-color-text-muted);
}

/* An open step's ring is drawn, not a glyph: the vendored font has no open circle. */
.setup__mark:empty::before {
  content: "";
  display: inline-block;
  width: 0.625rem;
  height: 0.625rem;
  border: 1.5px solid currentColor;
  border-radius: 50%;
}

.setup__step--done .setup__mark {
  color: var(--gf-color-success);
}

.setup__step--done .setup__label {
  color: var(--gf-color-text-muted);
}

.setup__label {
  flex: 1;
}

.setup__open {
  min-height: 32px;
}
</style>
